import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { Modal, Steps, Card, Typography, Tag, Button, Input, Select, Space, Tour, Alert, message, Spin } from 'antd';
import { 
  BookOutlined, 
  PlusOutlined, 
  RocketOutlined, 
  FolderOutlined, 
  SearchOutlined, 
  BulbOutlined, 
  DeleteOutlined, 
  InfoCircleOutlined,
  ExperimentOutlined,
  CodeOutlined,
  RobotOutlined,
  FormOutlined,
  MailOutlined,
  ShoppingOutlined,
  TeamOutlined,
  DatabaseOutlined,
  ApiOutlined,
  FileTextOutlined,
  UserOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { defaultTemplates } from './defaultTemplates';
import { adminInstance } from './api/axiosConfig';
import AIWorkflowGeneratorModal from './AIWorkflowGeneratorModal';
import { useReactFlow } from '@xyflow/react';

const { Step } = Steps;
const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

// Define category icons mapping
const categoryIcons = {
  'My Templates': <UserOutlined />,
  'Content Generation': <FileTextOutlined />,
  'Customer Service': <TeamOutlined />,
  'Data Processing': <DatabaseOutlined />,
  'E-commerce': <ShoppingOutlined />,
  'Email Marketing': <MailOutlined />,
  'Form Processing': <FormOutlined />,
  'Integration': <ApiOutlined />,
  'Code Generation': <CodeOutlined />,
  'Bot Creation': <RobotOutlined />
};

// Function to get icon for a category
const getCategoryIcon = (category) => {
  return categoryIcons[category] || <FolderOutlined />;
};

const TemplateModal = ({ 
  visible, 
  onCancel, 
  onSelectTemplate, 
  onCreateNew,
  workflowInstance,
  onImport
}) => {
  const { setNodes, setEdges } = useReactFlow();
  // State management
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: 'all',
    difficulty: 'all',
    search: ''
  });
  const [isTutorialMode, setIsTutorialMode] = useState(false);
  const [tutorialStep, setTutorialStep] = useState(0);
  const [allTemplates, setAllTemplates] = useState({});
  const [isAIModalVisible, setIsAIModalVisible] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isTemplateVisible, setIsTemplateVisible] = useState(visible);
  const [prompt, setPrompt] = useState('');
  const [isTemplateModalVisible, setIsTemplateModalVisible] = useState(visible);


  useEffect(() => {
    setIsTemplateModalVisible(visible);
  }, [visible]);

  useEffect(() => {
    setIsTemplateVisible(visible);
  }, [visible]);


  const showAIModal = () => {
    onCancel(); // Completely close template modal
    setIsAIModalVisible(true);
  };

  const handleAIModalClose = () => {
    setIsAIModalVisible(false);
    setIsTemplateVisible(true); // Show template modal again
  };


  const refs = {
    triggerNode: useRef(null),
    triggerTypeSelect: useRef(null),
    triggerInput: useRef(null),
    aiModelSidebar: useRef(null),
    aiModelNode: useRef(null),
    aiModelInput: useRef(null),
    outputSidebar: useRef(null),
    outputNode: useRef(null),
    saveButton: useRef(null),
    executeButton: useRef(null)
  };

  // Fetch user templates
  useEffect(() => {
    if (visible) {
      fetchUserTemplates();
    }
  }, [visible]);


  const fetchUserTemplates = async () => {
    setLoading(true);
    try {
      const response = await adminInstance.get('/templates');
      // Combine user templates with default templates
      const userTemplateObjects = response.data.reduce((acc, template) => ({
        ...acc,
        [`user-${template.id}`]: {
          ...template,
          isUserTemplate: true,
        }
      }), {});
      
      setAllTemplates({
        ...defaultTemplates,
        ...userTemplateObjects
      });
    } catch (error) {
      console.error('Failed to fetch user templates:', error);
      message.error('Failed to load saved templates');
      setAllTemplates(defaultTemplates);
    } finally {
      setLoading(false);
    }
  };

  // Get unique categories and difficulties from default templates
  const categories = useMemo(() => {
    const defaultCats = [...new Set(Object.values(defaultTemplates)
      .map(t => t.category)
      .filter(Boolean))];
    return ['My Templates', ...defaultCats];
  }, []);

  const difficulties = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

  // Filter templates
  const filteredTemplates = useMemo(() => {
    return Object.entries(allTemplates).filter(([id, template]) => {
      const searchTerm = filters.search.toLowerCase();
      const matchesCategory = 
        filters.category === 'all' || 
        (filters.category === 'My Templates' && template.isUserTemplate) ||
        (!template.isUserTemplate && template.category === filters.category);
      const matchesDifficulty = 
        filters.difficulty === 'all' || 
        (!template.isUserTemplate && template.difficulty === filters.difficulty);
      const matchesSearch = filters.search === '' || 
        template.name.toLowerCase().includes(searchTerm) ||
        (template.description || '').toLowerCase().includes(searchTerm) ||
        (template.tags || []).some(tag => tag.toLowerCase().includes(searchTerm));

      return matchesCategory && matchesDifficulty && matchesSearch;
    });
  }, [filters, allTemplates]);

  // Styles
  const styles = {
    sectionTitle: {
      padding: '16px 16px 0 16px',
      borderBottom: 'none',
      backgroundColor: '#fafafa',
      borderRadius: '8px 8px 0 0',
    },
    categoryTabs: {
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'center',
      padding: '12px 16px 0 16px',
      backgroundColor: '#fafafa',
      borderBottom: '1px solid #f0f0f0',
    },
    categoryButton: (isSelected) => ({
      margin: '0 6px 12px 0',
      padding: '4px 10px',
      height: 'auto',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      background: isSelected ? '#1890ff' : '#f0f0f0',
      color: isSelected ? 'white' : 'rgba(0, 0, 0, 0.65)',
      border: 'none',
      boxShadow: isSelected ? '0 2px 4px rgba(24, 144, 255, 0.2)' : 'none',
      transition: 'all 0.3s',
      fontSize: '13px',
    }),
    categoryIcon: {
      marginRight: 6,
      fontSize: 14,
    },
    modalBody: {
      maxHeight: '50vh',
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    filterBar: {
      padding: '16px',
      borderBottom: '1px solid #f0f0f0',
      backgroundColor: '#fafafa',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    templatesGrid: {
      padding: '16px',
      overflowY: 'auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
      gap: '16px',
      maxHeight: '500px'
    },
    templateCard: {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between'
    }
  };

  // Template Card Component
  const TemplateCard = ({ id, template }) => {
    const isUserTemplate = template.isUserTemplate;
    
    return (
      <Card
        hoverable
        style={styles.templateCard}
        actions={[
          <Button type="primary" onClick={() => onSelectTemplate(id, !isUserTemplate)}>
            Use Template
          </Button>,
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={() => handleDeleteTemplate(id)}
            disabled={!isUserTemplate}
          >
            Delete
          </Button>
        ]}
      >
        <div>
          {isUserTemplate ? (
            <Tag color="magenta">My Template</Tag>
          ) : (
            <>
              <Tag color="blue">{template.category}</Tag>
              {template.difficulty && (
                <Tag color={getDifficultyColor(template.difficulty)}>
                  {template.difficulty}
                </Tag>
              )}
            </>
          )}
          <Title level={5}>{template.name}</Title>
          <Text type="secondary">{template.description}</Text>
          {template.tags?.length > 0 && (
            <div style={{ marginTop: 8 }}>
              {template.tags.map(tag => (
                <Tag key={tag}>{tag}</Tag>
              ))}
            </div>
          )}
        </div>
      </Card>
    );
  };

  // Delete user template
  const handleDeleteTemplate = async (templateId) => {
    try {
      const actualId = templateId.replace('user-', '');
      await adminInstance.delete(`/templates/${actualId}`);
      message.success('Template deleted successfully');
      fetchUserTemplates();
    } catch (error) {
      console.error('Delete template error:', error);
      message.error('Failed to delete template');
    }
  };

  const tutorialSteps = useMemo(() => [
    {
      title: 'Welcome to Workflow Builder',
      target: () => document.querySelector('.reactflow-wrapper'),
      placement: 'right-start',
      description: (
        <Space direction="vertical">
          <Text>This is your workflow canvas. Notice the Trigger node already placed for you - this is where every workflow begins!</Text>
          <Text type="secondary">The sidebar on the left contains all available nodes you can add to your workflow.</Text>
        </Space>
      )
    },
    {
      title: 'Configure Trigger',
      target: () => document.querySelector('.trigger-type-select'),
      placement: 'right',
      description: (
        <Space direction="vertical">
          <Text>First, let's configure our trigger. Click on the Trigger Type dropdown and select "Manual Trigger".</Text>
          <Text type="secondary">Manual triggers are perfect for testing workflows or when you want to start them yourself.</Text>
        </Space>
      )
    },
    {
      title: 'Add Test Input',
      target: () => document.querySelector('.trigger-input'),
      placement: 'right',
      description: (
        <Space direction="vertical">
          <Text>Type some test input in the field. For example, try typing "Apple".</Text>
          <Text type="secondary">This will be our test input that we'll process with AI in the next steps.</Text>
        </Space>
      )
    },
    {
      title: 'AI Actions Menu',
      target: () => document.querySelector('.ai-actions-menu'),
      placement: 'bottomRight',
      
      description: (
        <Space direction="vertical">
          <Text>Hover on "AI Actions" in the sidebar to reveal the available AI nodes.</Text>
          <Text>From the AI Actions menu, drag the "AI Model" node to your canvas.</Text>
          <Text type="secondary">This menu contains different types of AI operations you can add to your workflow.</Text>
        </Space>
      ),
      mask: false,
    },
    {
      title: 'Connect the Nodes',
      target: () => document.querySelector('.react-flow__handle-right'),
      placement: 'right',
      description: (
        <Space direction="vertical">
          <Text>Connect the Trigger to the AI Model by dragging a line between them.</Text>
          <Text type="secondary">Click and hold the right handle of the Trigger node and drag to the left handle of the AI Model node.</Text>
        </Space>
      )
    },
    {
      title: 'Configure AI Model',
      target: () => document.querySelector('.ai-model-node'),
      placement: 'right',
      description: (
        <Space direction="vertical">
          <Text>First, choose an AI model from the list. You have to setup your OpenAI or OpenRouter API from the settings to be able to use the models.</Text>
          <Text>Now let's write our AI prompt. Click the "Insert Input" button below the text area and select '[Input from trigger-1]'.</Text>
          <Text>Then add: "Write 2 sentences about"</Text>
          <Text type="secondary">Your prompt should read: "Write 2 sentences about [Input from trigger-1]"</Text>
        </Space>
      )
    },
    {
      title: 'Add Output Node',
      target: () => document.querySelector('.node-selector-output'),
      placement: 'bottomRight',
      description: (
        <Space direction="vertical">
          <Text>Add an Output node from the sidebar. Place it to the right of the AI Model.</Text>
          <Text type="secondary">Output nodes determine what happens with your AI-generated content.</Text>
        </Space>
      ),
      mask: false
    },
    {
      title: 'Configure Output',
      target: () => document.querySelector('.output-selector'),
      placement: 'right',
      description: (
        <Space direction="vertical">
          <Text>Now let's configure our Output. "Display" is the default option. For this example let's use that.</Text>
          <Text type="secondary">Display output is a great option when you want to execute a workflow and immediately see the result in the workflow builder screen.</Text>
        </Space>
      )
    },
    {
      title: 'Connect to Output',
      target: () => document.querySelector('.ai-model-handle'),
      placement: 'right',
      description: (
        <Space direction="vertical">
          <Text>Connect the AI Model to the Output node.</Text>
          <Text type="secondary">The connection between the two nodes carry data between them. So if you want a node to receive data from another node, you need to connect them.</Text>
        </Space>
      ) 
      
    },
    {
      title: 'Save Workflow',
      target: () => document.querySelector('.save-workflow-button'),
      placement: 'right',
      description: (
        <Space direction="vertical">
          <Text>Let's save your workflow. Click the "Save Workflow" button.</Text>
          <Text>Then when the box opens, give your workflow a name. This will help you identify it later.</Text>
        </Space>
      )
    },
    {
      title: 'Execute Workflow',
      target: () => document.querySelector('.execute-workflow-button'),
      placement: 'right',
      description: (
        <Space direction="vertical">
          <Text>Now we're ready to run our workflow! Click the "Execute Workflow" button.</Text>
          <Text type="secondary">You'll see the AI generate two sentences about your input in the Output node.</Text>
        </Space>
      )
    },
    {
      title: 'Congratulations! 🎉',
      target: null,
      placement: 'right-end',
      description: (
        <Space direction="vertical">
          <Text>You've created your first AI workflow!</Text>
          <Text type="secondary">Now you can:</Text>
          <ul>
            <li>Try different inputs in the Trigger node</li>
            <li>Modify the AI prompt</li>
            <li>Change the output type to save to a post or send via email</li>
            <li>Explore other nodes in the sidebar</li>
          </ul>
        </Space>
      )
    },
    {
      title: 'Learn More',
      target: null,
      placement: 'bottom',
      description: (
        <Space direction="vertical">
          <Text>Great job completing the tutorial! 🎉</Text>
          <Text>To learn more about:</Text>
          <ul>
            <li>Different node types and their capabilities</li>
            <li>Advanced workflow execution logic</li>
            <li>Best practices and optimization tips</li>
            <li>Integration examples and use cases</li>
          </ul>
          <Text>Visit our comprehensive documentation:</Text>
          <Button 
            type="primary" 
            icon={<BookOutlined />}
            onClick={() => window.open('https://wpaiworkflowautomation.com/docs/', '_blank')}
          >
            View Documentation
          </Button>
          <Text type="secondary">
            Pro tip: Bookmark the documentation for quick reference while building your workflows!
          </Text>
        </Space>
      )
    }
  ], []);


  const startTutorial = async () => {
    onCreateNew(); // Create new workflow with trigger node
    setIsTutorialMode(true);
    onCancel(); // Close the template modal
  };

  const handleGenerateWorkflow = async (prompt) => {
    if (!prompt.trim()) {
      message.error('Please enter a workflow description');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await adminInstance.post(
        '/generate-workflow',
        { prompt },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.data.success && response.data.error) {
        // Handle structured error response
        throw new Error(response.data.error.message || 'Failed to generate workflow');
      }

      const workflow = response.data;
      
      // Validate workflow structure
      if (!workflow.nodes || !workflow.edges) {
        throw new Error('The generated workflow structure was invalid. Please try again with a different description.');
      }

      // Check for empty workflow
      if (workflow.nodes.length === 0) {
        throw new Error('No workflow nodes were generated. Please try with a more specific description.');
      }

      onImport(workflow);
      message.success('Workflow generated successfully!');
      setIsAIModalVisible(false);
      onCancel();

    } catch (error) {
      console.error('Error generating workflow:', error);
      
      // Handle different types of errors with meaningful messages
      let errorMessage = 'Failed to generate workflow: ';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        if (error.response.data && error.response.data.error) {
          errorMessage += error.response.data.error.message || 'Server error occurred';
        } else if (error.response.status === 500) {
          errorMessage = 'The workflow generation failed. This could be due to:' +
                        '\n- Complex or ambiguous requirements' +
                        '\n- Server processing limitations' +
                        '\n\nPlease try again with a simpler or more specific description.';
        } else {
          errorMessage += `Server error (${error.response.status})`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage += 'No response from server. Please check your connection.';
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMessage += error.message || 'An unexpected error occurred';
      }

      // Show error with more detailed message
      Modal.error({
        title: 'Workflow Generation Failed',
        content: (
          <div>
            <p>{errorMessage}</p>
            <Alert
              type="info"
              message="Tips for successful workflow generation:"
              description={
                <ul>
                  <li>Be specific about what you want the workflow to do</li>
                  <li>Explain your goal, you don't need to specify the step, let the bot do it</li>
                  <li>Specify input sources and desired outputs clearly, do you want to receive data in a form? then mention it.</li>
                </ul>
              }
            />
          </div>
        ),
        width: 500
      });
    } finally {
      setIsGenerating(false);
    }
};

  const handleLoadWorkflow = (workflowData) => {
    try {
      // Process nodes to ensure they have all required properties
      const processedNodes = workflowData.nodes.map(node => ({
        ...node,
        type: node.type,
        position: node.position,
        data: {
          ...node.data,
          updateNodeData: (nodeId, newData) => {
            setNodes(nds => 
              nds.map(n => n.id === nodeId ? { ...n, data: { ...n.data, ...newData } } : n)
            );
          },
          onDelete: (nodeId) => {
            setNodes(nds => nds.filter(n => n.id !== nodeId));
          }
        },
        draggable: true,
        connectable: true,
      }));

      // Process edges to ensure they have all required properties
      const processedEdges = workflowData.edges.map(edge => ({
        ...edge,
        animated: false,
        style: { stroke: '#b1b1b7' },
      }));

      // Set the nodes and edges
      setNodes(processedNodes);
      setEdges(processedEdges);

    } catch (error) {
      console.error('Error loading workflow:', error);
      message.error('Failed to load workflow');
    }
  };

  return (
    <>
      <Modal
        title={
          <div style={{ textAlign: 'center' }}>
            <RocketOutlined style={{ fontSize: 24, marginBottom: 3 }} />
            <Title level={4}>Welcome to AI Workflow Automation</Title>
            <Space size="middle">
              <Button 
                type="primary"
                icon={<PlusOutlined />}
                onClick={onCreateNew}
              >
                Start from Scratch
              </Button>
              <Button 
                icon={<ExperimentOutlined />}
                onClick={showAIModal}
                style={{ 
                  background: 'linear-gradient(45deg, #FF6B6B, #4ECDC4)',
                  color: 'white',
                  border: 'none'
                }}
              >
                Generate with AI
              </Button>
              <Button 
                type="default"
                icon={<BulbOutlined />}
                onClick={startTutorial}
              >
                Interactive Tutorial
              </Button>
            </Space>
          </div>
        }
        open={isTemplateVisible && !isAIModalVisible} 
        onCancel={onCancel}
        footer={null}
        width={1000}
      >
        <div style={styles.sectionTitle}>
          <Title level={5} style={{ margin: 0, color: '#1f1f1f' }}>
            Start from a template
          </Title>
        </div>

        {/* Visual Categories Tabs */}
        <div style={styles.categoryTabs}>
          <Button
            style={styles.categoryButton(filters.category === 'all')}
            onClick={() => setFilters({ ...filters, category: 'all' })}
          >
            <FolderOutlined style={styles.categoryIcon} /> All Categories
          </Button>
          
          {categories.map(category => (
            <Button
              key={category}
              style={styles.categoryButton(filters.category === category)}
              onClick={() => setFilters({ ...filters, category: category })}
            >
              {getCategoryIcon(category)} {category}
            </Button>
          ))}
        </div>

        <div style={styles.filterBar}>
          <Space size="middle">
            <Input
              placeholder="Search templates..."
              prefix={<SearchOutlined />}
              style={{ width: 250 }}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />

            <Select
              style={{ width: 200 }}
              placeholder="Select Difficulty"
              value={filters.difficulty}
              onChange={(value) => setFilters({ ...filters, difficulty: value })}
            >
              <Option value="all">All Difficulties</Option>
              {difficulties.map(diff => (
                <Option key={diff} value={diff}>{diff}</Option>
              ))}
            </Select>
          </Space>
        </div>

        <div style={styles.templatesGrid}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spin size="large" />
            </div>
          ) : filteredTemplates.length > 0 ? (
            filteredTemplates.map(([id, template]) => (
              <TemplateCard key={id} id={id} template={template} />
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', gridColumn: '1 / -1' }}>
              <InfoCircleOutlined style={{ fontSize: 36, color: '#bfbfbf', marginBottom: 16 }} />
              <Text style={{ display: 'block', color: '#8c8c8c' }}>
                No templates found for your selection. Try changing your filters.
              </Text>
            </div>
          )}
        </div>
      </Modal>

      {isTutorialMode && (
        <Tour
          open={true}
          onClose={() => {
            setIsTutorialMode(false);
            setTutorialStep(0);
          }}
          current={tutorialStep}
          onChange={setTutorialStep}
          steps={tutorialSteps}
          zIndex={1000}
          scrollIntoViewOptions={{
            block: 'nearest',
            inline: 'nearest',
            behavior: 'smooth'
          }}
        />
      )}

      <AIWorkflowGeneratorModal 
        visible={isAIModalVisible}
        onCancel={() => {
          setIsAIModalVisible(false);
          // Don't reopen template modal
        }}
        onGenerate={handleGenerateWorkflow}
      />
    </>
  );
};

const getDifficultyColor = (difficulty) => {
  const colors = {
    'Beginner': 'green',
    'Intermediate': 'blue',
    'Advanced': 'purple',
    'Expert': 'red'
  };
  return colors[difficulty] || 'blue';
};

export default TemplateModal;